'use client';

import Image from 'next/image';
import Link from 'next/link';
import { motion, useScroll, useSpring, useTransform } from 'framer-motion';
import { HTMLProps, ReactNode } from 'react';
import {
  IconArrowRight,
  IconBorderCorners,
  IconCircleArrowRightFilled,
  IconSpeakerphone,
} from '@tabler/icons-react';
import { IconBase } from '@artilleryio/alpha-centauri-icons';
import Accordion from '@/components/Accordion';
import Button from '@/components/Button';
import Hero from '@/components/Hero';
import { integrations } from '@/data/integrations';

export default function Page() {
  const { scrollYProgress } = useScroll();
  const springConfig = {
    mass: 1,
    stiffness: 200,
    damping: 50,
  };
  const scrollSpring = useSpring(scrollYProgress, springConfig);

  return (
    <>
      <div className="container">
        <Hero
          title="The Complete Load Testing Platform"
          description="Artillery is a scalable, flexible and easy-to-use platform that contains everything you need for production-grade load testing."
          size="lg"
          announcement={
            <Link
              href="/blog/announcing-playwright-test-reporter"
              className="group relative -mt-5 mb-5 inline-flex items-center gap-1.5 rounded-full border border-jadeA-2 bg-jadeA-2 px-3 py-1 text-sm text-jade-10 backdrop-blur transition hover:bg-jade-3 hover:text-jade-11"
            >
              <IconSpeakerphone
                size={16}
                stroke={1.5}
                className="shrink-0 -rotate-6 fill-jadeA-3 transition group-hover:-rotate-12"
              />
              Announcing Playwright Test Runner reporting in Artillery Cloud
              <IconCircleArrowRightFilled
                size={16}
                stroke={1.5}
                className="-mr-0.5 shrink-0 transition group-hover:translate-x-0.5"
              />
            </Link>
          }
        >
          <div className="flex flex-wrap justify-center gap-3">
            <Button href="https://app.artillery.io/" variant="primary">
              Get started — free
            </Button>
            <Button
              target="_blank"
              href="https://cal.com/team/artillery/30m"
              variant="secondary"
            >
              Talk to sales
            </Button>
          </div>

          <p className="mt-3 w-full text-sm">
            Need help with an internal <abbr title="Proof of concept">PoC</abbr>
            ?{' '}
            <Link
              href="https://cal.com/team/artillery/poc-consultation-25m"
              className="text-body underline decoration-neutral-6 underline-offset-2 transition-colors hover:text-contrast hover:decoration-neutral-12"
              target="_blank"
            >
              Talk to an engineer
            </Link>
          </p>
        </Hero>
      </div>

      <section className="relative mx-auto mb-12 flex w-11/12 max-w-screen-2xl flex-col justify-center [perspective:800px] md:mb-24 md:flex-row">
        <motion.figure
          className="mx-auto w-11/12 rounded-md border border-slate-3 bg-slate-2 shadow-lg sm:w-11/12"
          whileHover={{ scale: 1.01 }}
          transition={springConfig}
          style={{
            y: useTransform(scrollSpring, [0, 1], [0, -100]),
            rotateX: useTransform(scrollSpring, [0, 1], [0, 12]),
          }}
        >
          <FrameHeader>Artillery Cloud</FrameHeader>
          <video
            className="aspect-video rounded-md"
            src="/images/artillery-cloud.mp4"
            poster="/images/artillery-cloud@2x.png"
            width={3068}
            height={1728}
            controls={false}
            loop
            autoPlay
            playsInline
            muted
          />
        </motion.figure>

        <motion.blockquote
          className="group -left-2 bottom-28 -mt-16 flex w-10/12 min-w-56 flex-col gap-3 text-pretty rounded-md border border-l-4 border-orangeA-3 border-l-orange-9 bg-orangeA-2 p-6 text-sm shadow-2xl backdrop-blur-2xl sm:max-w-sm sm:p-8 md:absolute md:mt-0 md:max-w-md lg:bottom-32 lg:p-10 lg:text-base xl:bottom-40"
          whileHover={{ scale: 1.01 }}
          transition={springConfig}
          style={{
            y: useTransform(scrollSpring, [0, 1], [0, -150]),
            rotateX: useTransform(scrollSpring, [0, 1], [0, 10]),
          }}
        >
          <p className="text-orange-12 *:font-medium *:text-contrast">
            Learn how <b>Code Wizards</b> load tested Heroic Labs’ Nakama to{' '}
            <b>two million concurrent players</b> with Artillery and AWS.
          </p>
          <div className="flex items-center gap-5">
            <Button
              href="https://aws.amazon.com/blogs/gametech/how-heroic-labs-load-tested-nakama-to-two-million-concurrent-players-with-aws/"
              target="_blank"
              className="border-orange-5 bg-orange-4 text-orange-11 hover:border-orange-7 hover:bg-orange-6 hover:text-orange-12"
            >
              Read blog post ↗
            </Button>
            <Link
              href="https://aws.amazon.com/blogs/gametech/how-heroic-labs-load-tested-nakama-to-two-million-concurrent-players-with-aws/"
              className="flex items-center gap-2"
            >
              <Image
                src="/images/logos/aws.svg"
                width={44}
                height={26}
                alt="AWS"
              />
            </Link>
          </div>
        </motion.blockquote>

        <motion.blockquote
          className="group -bottom-12 order-last -mt-3 ml-auto flex w-10/12 min-w-56 flex-col gap-3 text-pretty rounded-md border border-l-4 border-indigoA-3 border-l-indigo-9 bg-indigoA-2 p-6 text-sm shadow-2xl backdrop-blur-2xl sm:max-w-sm sm:p-8 md:absolute md:max-w-md lg:p-10 lg:text-base"
          whileHover={{ scale: 1.01 }}
          transition={springConfig}
          style={{
            y: useTransform(scrollSpring, [0, 1], [0, -50]),
            rotateX: useTransform(scrollSpring, [0, 1], [0, 3]),
          }}
        >
          <p className="text-indigo-12 *:font-medium *:text-contrast">
            Learn how <b>Okta Fine Grained Authorization</b> ensures high
            availability, low latency, and unlimited scale with load testing
            with Artillery.
          </p>
          <div className="flex items-center gap-5">
            <Button
              href="https://auth0.com/blog/getting-unlimited-scalability-with-okta-fine-grained-authorization/"
              variant="primary"
              target="_blank"
              className="shrink-0"
            >
              Read blog post ↗
            </Button>
            <Link href="https://auth0.com/blog/getting-unlimited-scalability-with-okta-fine-grained-authorization/">
              <Image
                src="/images/logos/okta.svg"
                width={80}
                height={26}
                alt="Okta"
                className="shrink"
              />
            </Link>
          </div>
        </motion.blockquote>

        <motion.figure
          className="absolute bottom-8 my-4 hidden w-4/12 min-w-56 max-w-96 origin-center rounded-md border border-slate-4 bg-slate-3 shadow-xl md:-right-4 md:block"
          whileHover={{ scale: 1.01 }}
          transition={springConfig}
          style={{
            y: useTransform(scrollSpring, [0, 1], [0, -250]),
            rotateX: useTransform(scrollSpring, [0, 1], [0, 6]),
          }}
        >
          <FrameHeader>Artillery CLI</FrameHeader>
          <div className="p-2.5 lg:p-5">
            <Image
              alt="Artillery CLI"
              src="/images/artillery-cli.svg"
              width={361}
              height={196}
              priority
              className="h-auto w-full"
            />
          </div>
        </motion.figure>
      </section>

      <section className="container mb-24 text-center">
        <h2 className="mb-8 text-balance font-medium text-muted">
          Powering load testing at industry-leading companies
        </h2>
        <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-6 lg:gap-y-4">
          <Image
            src="/images/logos/ia-interactive.svg"
            alt="IA Interactive"
            width={144}
            height={28}
          />
          <Image
            src="/images/logos/amity.svg"
            alt="Amity"
            width={92}
            height={28}
          />
          <Image
            src="/images/logos/gymshark.svg"
            alt="Gymshark"
            width={116}
            height={28}
          />
          <Image
            src="/images/logos/big-give.svg"
            alt="Big Give"
            width={110}
            height={28}
          />
          <div aria-hidden className="hidden w-full lg:block 2xl:hidden" />
          <Image
            src="/images/logos/brave.svg"
            alt="Brave"
            width={94}
            height={28}
          />
          <Image
            src="/images/logos/housing-cloud.svg"
            alt="Housing.Cloud"
            width={108}
            height={28}
          />
          <Image
            src="/images/logos/evervault.svg"
            alt="Evervault"
            width={128}
            height={28}
          />
        </div>
      </section>

      <section
        id="features"
        className="border-t border-slateA-3 bg-gradient-to-br from-slateA-2 py-20"
      >
        <div className="container">
          <SectionHeader>
            Everything you need for <span>production-grade load testing</span>
          </SectionHeader>

          <div className="grid grid-cols-1 gap-6 text-pretty *:flex *:flex-col *:gap-1 *:rounded-lg *:border *:border-slate-3 *:px-6 *:py-4 odd:*:bg-subtle sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <h3 className="text-lg font-medium text-contrast">
                Distributed load testing
              </h3>
              <p>
                Artillery can scale out your load tests with serverless load
                generators that run in your own AWS or Azure account. There is
                no long-lived infrastructure for you to set up or manage.
              </p>
              <p>
                <Link
                  href="/docs/load-testing-at-scale"
                  className="inline-flex gap-1 font-mono text-xs uppercase text-primary-9 transition-colors hover:text-primary-11"
                >
                  Learn more
                  <IconArrowRight size={14} stroke={1.5} />
                </Link>
              </p>
            </div>
            <div>
              <h3 className="text-lg font-medium text-contrast">
                Load test anything
              </h3>
              <p>
                Load test web apps, HTTP APIs, GraphQL services, WebSockets,
                Socket.IO, SOAP, gRPC, Kafka, and more.
              </p>
              <p>
                <Link
                  href="/docs/get-started/why-artillery#what-kinds-of-services-can-i-test-with-artillery"
                  className="inline-flex gap-1 font-mono text-xs uppercase text-orange-9 transition-colors hover:text-orange-11"
                >
                  Learn more
                  <IconArrowRight size={14} stroke={1.5} />
                </Link>
              </p>
            </div>
            <div>
              <h3 className="text-lg font-medium text-contrast">
                Load test with Playwright
              </h3>
              <p>
                Launch thousands of headless browsers with Playwright code. See
                how your web app handles high load, and the impact on Core Web
                Vitals.
              </p>
              <p>
                <Link
                  href="/docs/reference/engines/playwright"
                  className="inline-flex gap-1 font-mono text-xs uppercase text-teal-9 transition-colors hover:text-teal-11"
                >
                  Learn more
                  <IconArrowRight size={14} stroke={1.5} />
                </Link>
              </p>
            </div>
            <div>
              <h3 className="text-lg font-medium text-contrast">
                Reporting &amp; collaboration
              </h3>
              <p>
                Visualize test results, share reports, analyze performance
                trends, and collaborate with your team.
              </p>
              <p>
                <Link
                  href="https://app.artillery.io/"
                  className="inline-flex gap-1 font-mono text-xs uppercase text-teal-9 transition-colors hover:text-teal-11"
                >
                  Get started
                  <IconArrowRight size={14} stroke={1.5} />
                </Link>
              </p>
            </div>
            <div>
              <h3 className="text-lg font-medium text-contrast">
                Lots of integrations
              </h3>
              <p>
                20+ integrations and plugins for monitoring, observability
                (including OpenTelemetry), CI/CD, and IDEs (VS Code, WebStorm,
                etc).
              </p>
              <p>
                <Link
                  href="/docs/resources/integrations"
                  className="inline-flex gap-1 font-mono text-xs uppercase text-primary-9 transition-colors hover:text-primary-11"
                >
                  Learn more
                  <IconArrowRight size={14} stroke={1.5} />
                </Link>
              </p>
            </div>
            <div>
              <h3 className="text-lg font-medium text-contrast">
                Scalable and cost-efficient
              </h3>
              <p>
                Artillery executes tests in your own AWS or Azure account, which
                is is orders of magnitude cheaper than running tests on a hosted
                platform. It’s serverless, so you only pay for what you use, and
                there is no infrastructure to manage.
              </p>
              <p>
                <Link
                  href="/docs/load-testing-at-scale"
                  className="inline-flex gap-1 font-mono text-xs uppercase text-orange-9 transition-colors hover:text-orange-11"
                >
                  Learn more
                  <IconArrowRight size={14} stroke={1.5} />
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      <section
        id="integrations"
        className="border-t border-slateA-3 bg-gradient-to-br from-slateA-2 py-20"
      >
        <div className="container">
          <SectionHeader>
            Lots of <span>Integrations</span>
          </SectionHeader>
          <div className="grid grid-cols-2 gap-3 text-center font-mono text-xs uppercase text-contrast sm:grid-cols-3 md:grid-cols-4 md:gap-6 lg:grid-cols-5">
            {integrations.map(
              ({ name, href, Icon, viewBox, backgroundColor }) => (
                <Link
                  key={name}
                  href={href}
                  target="_blank"
                  className="saturate-75 group flex flex-col items-center rounded-md border border-whiteA-2 transition-all duration-500 hover:brightness-125 hover:saturate-100"
                  style={{ backgroundColor }}
                >
                  <IconBase
                    name={name}
                    width="40"
                    height="40"
                    color="white"
                    viewBox={viewBox}
                    helperClasses="my-4 group-hover:scale-110 duration-500 transition"
                  >
                    <Icon />
                  </IconBase>
                  <figcaption className="w-full rounded-b-md border-t border-whiteA-2 py-2">
                    {name}
                  </figcaption>
                </Link>
              ),
            )}
            <Link
              href="/docs/resources/integrations"
              className="saturate-75 group flex flex-col items-center rounded-md border border-whiteA-2 text-primary-11 transition-all duration-500 hover:bg-primary-5 hover:text-primary-12"
            >
              <div className="my-4 duration-500 group-hover:scale-110">
                <IconBorderCorners stroke={1.5} size={40} />
              </div>
              <figcaption className="mx-px w-full rounded-b-md border-t border-whiteA-2 py-2">
                Browse all ↗
              </figcaption>
            </Link>
          </div>
        </div>
      </section>

      <section
        id="faq"
        className="border-t border-slateA-3 bg-gradient-to-br from-slateA-2 py-20"
      >
        <div className="container grid grid-cols-1 lg:grid-cols-3 lg:gap-6">
          <div>
            <SectionHeader>
              Frequently Asked <span>Questions</span>
            </SectionHeader>
          </div>
          <div className="lg:col-span-2 lg:-mt-3">
            <Accordion title="Does Artillery Cloud execute our tests?">
              <p>
                All of your team’s tests run in your own AWS or Azure account,
                with all of the cost, security, and governance benefits that
                brings.
              </p>
              <p>
                Artillery takes care of creating and managing all the necessary
                cloud infrastructure to execute your tests. The setup is quick,
                and you only pay for what you use. There is no long-running
                infrastructure to manage.
              </p>

              <p>
                Hosted load runners are on our roadmap. If you’d like early
                access, please get in touch on{' '}
                <a href="mailto:sales@artillery.io?subject=Hosted runners early access">
                  sales@artillery.io
                </a>
              </p>
            </Accordion>
            <Accordion title="What’s the pricing model?">
              <p>
                Artillery Cloud offers a free plan for teams just starting with
                load testing, or teams working on an internal proof-of-concept
                project.
              </p>
              <p>
                For teams running continuous load tests on production apps,
                prices start at <strong>$499/month</strong>.
              </p>

              <p>
                We support a variety of payment methods (card, invoice, ACH) and
                are launching an AWS Marketplace offering soon.
              </p>
              <p>
                Please get in touch on{' '}
                <Link href="mailto:sales@artillery.io">sales@artillery.io</Link>{' '}
                if you have any questions.
              </p>
            </Accordion>
            <Accordion title="Can we run Artillery Cloud in our VPC?">
              <p>
                Yes, we offer an Enterprise subscription plan with the ability
                to run Artillery Cloud in your own VPC. Please{' '}
                <Link href="https://cal.com/team/artillery/30m">
                  book a discovery call
                </Link>{' '}
                to discuss requirements and request a pricing quote.
              </p>
            </Accordion>
          </div>
        </div>
      </section>

      <section
        id="manifesto"
        className="border-t border-slateA-3 bg-gradient-to-br from-slateA-2 py-20"
      >
        <div className="container grid grid-cols-1 lg:grid-cols-3 lg:gap-6">
          <div>
            <SectionHeader>
              The <span>Artillery Manifesto</span>
            </SectionHeader>
          </div>
          <div className="prose lg:col-span-2 lg:mt-1.5">
            <p>It’s time for a rethink.</p>
            <p>
              <strong>Load testing as practiced today does not work.</strong>
            </p>
            <p>
              As engineers, we want to build fast, reliable, and scalable
              systems that serve our users.
            </p>
            <p>
              Traffic spikes, new code deployments, new features, and new
              dependencies can all cause production problems.{' '}
              <strong>
                Load testing is one of the most effective tools for keeping our
                apps fast and reliable.
              </strong>{' '}
              And yet, we often leave load testing until it’s too late, and
              cross our fingers & hope for the best.
            </p>
            <p>
              Why is that? <strong>The tools fail us</strong>. Clunky,
              antiquated, and hard to use. Open source tools that require
              managing non-trivial infrastructure to test at scale. Commercial
              tools that are so expensive you end up load testing once a
              quarter.
            </p>
            <p>
              Companies like AWS, Netflix and Slack invest into internal load
              testing platforms to make load testing{' '}
              <strong>as easy as unit and integration testing</strong>. Most
              teams did not have access to such tools - until Artillery.
            </p>
            <p>
              <strong>Artillery is a fresh take on load testing</strong>,
              informed by years of experience of production engineering and SRE.
              Modern load testing should be easy to get started with. It should
              be cloud-native and serverless, so that you can{' '}
              <strong>forget about infrastructure</strong>, and just run your
              load tests, <strong>at any scale</strong>. It should be designed
              for <strong>continuous testing</strong>, and integrate with your
              entire CI/CD and observability stack.
            </p>
            <p>
              With Artillery we’re building a world-class load testing platform
              for everybody. We want every developer team to be able to go from{' '}
              <strong>zero to production-scale testing, fast</strong>.
            </p>
            <p>Load testing the way it should be in 2025.</p>
            <p>
              <svg
                aria-label="Team Artillery"
                width="129"
                height="27"
                className="mt-7 fill-current"
              >
                <path d="M5.725 5.525q-.45 0-1.25-.05a22 22 0 0 1-1.525-.2q-.75-.125-1.15-.35a4.7 4.7 0 0 1-.95-.775Q.4 3.7.4 3.1q0-.725.6-1.275.6-.575 1.475-.95Q3.375.5 4.3.3 5.226.075 5.875.05q.3-.025.575-.025h.6q2.75 0 5.475.575T17.9 1.8q1.65.4 3.4.75t3.475.35q.275 0 .7-.025.424-.05.75-.2.35-.15.35-.475 0-.25-.175-.35-.15-.125-.325-.2-.175-.1-.175-.275 0-.15.125-.275a.5.5 0 0 1 .325-.125q.25 0 .525.225.3.225.5.525.225.3.225.55a.96.96 0 0 1-.5.875 3.5 3.5 0 0 1-1.175.375q-.65.075-1.125.075-1.725 0-3.475-.35a102 102 0 0 1-3.4-.75q-2.625-.625-5.35-1.175A26 26 0 0 0 7.125.75h-.6q-.275 0-.575.025-.7.05-1.625.275-.925.2-1.75.575-.8.375-1.175.95-.175.25-.175.475 0 .4.35.725.375.326.725.525.325.2.975.325.675.1 1.35.15.7.025 1.075.025 1.025 0 2.025-.125a15 15 0 0 0 1.95-.375q.15-.05.5-.175t.75-.25q.425-.15.7-.25.3-.125.325-.125.2 0 .325.15a.38.38 0 0 1 .125.275q0 .176-.25.3a7 7 0 0 1-1.075.45q-.6.175-1.175.325-2.025.525-4.175.525M9.275 18q-.4 0-.4-.375 0-.275.125-.65l.25-.625q.6-1.475 1.4-2.925.825-1.45 1.6-2.875.225-.425.675-1.2.45-.8.9-1.7.475-.925.8-1.7.35-.775.35-1.175a.36.36 0 0 0-.05-.2.44.44 0 0 1-.05-.2q0-.125.125-.225a.47.47 0 0 1 .3-.1q.325 0 .4.25t.075.475q0 .5-.325 1.325a22 22 0 0 1-.8 1.725q-.45.9-.9 1.725-.45.8-.725 1.3-.776 1.4-1.575 2.825-.8 1.425-1.4 2.9l-.2.55-.15.55Q9.625 18 9.275 18m8.83-.75q-1.3 0-1.825-.75-.5-.776-.5-1.8 0-.75.375-1.775.375-1.05 1.025-2.05a6.7 6.7 0 0 1 1.55-1.65q.874-.675 1.875-.675.65 0 1.025.375.4.35.4.9 0 1.05-.675 2.025a5.4 5.4 0 0 1-1.75 1.6 4.5 4.5 0 0 1-2.3.625h-.3l-.3-.05q-.075.325-.075.75 0 .65.3 1.2.325.55 1.225.55.75 0 1.45-.375t1.175-.85q.474-.475.8-.85t.675-.85q.35-.5.875-1.3a.32.32 0 0 1 .3-.175q.35 0 .35.375 0 .125-.05.2-.675 1.15-1.5 2.2a8 8 0 0 1-1.85 1.7q-1 .65-2.275.65m-.625-3.95q.95 0 1.775-.5a4.5 4.5 0 0 0 1.375-1.3q.55-.776.55-1.575a.67.67 0 0 0-.15-.45q-.125-.175-.45-.175-.65 0-1.25.45-.6.425-1.1 1.1a11 11 0 0 0-.825 1.3q-.325.65-.475 1.1l.25.05zm6.014 3.8q-.825 0-1.15-.425-.3-.45-.3-1.075 0-.6.2-1.25t.425-1.2q.25-.575.4-.85.45-.775 1-1.5a9 9 0 0 1 1.225-1.275 5.2 5.2 0 0 1 1.325-.875 3.3 3.3 0 0 1 1.4-.325q1.2 0 1.7.65.5.626.5 1.35 0 .25-.075.525a3 3 0 0 1-.25.65q-.126.275-.325.275-.175 0-.325-.175t-.1-.35a3 3 0 0 1 .125-.35 1 1 0 0 0 .125-.35q.024-.125.025-.225a.8.8 0 0 0 .025-.2q0-.675-.45-.925a1.67 1.67 0 0 0-.925-.275q-.576 0-1.2.325-.6.326-1.05.75-.55.55-1.1 1.275t-.95 1.475a15 15 0 0 1-.275.6q-.2.475-.4 1.075a3.7 3.7 0 0 0-.175 1.075q0 .35.15.6.174.25.575.25.425 0 .975-.4a8.5 8.5 0 0 0 1.15-.975 22 22 0 0 0 1.775-2q.15-.2.275-.425.15-.25.3-.45.075-.1.3-.1a.75.75 0 0 1 .4.125q.225.1.175.3-.1.175-.375.775-.25.575-.475 1.275-.225.675-.225 1.2 0 .65.4.75.275.05.575-.2.324-.25.575-.575l.4-.475q.525-.7.975-1.425.474-.75.95-1.4.15-.2.35-.2.35 0 .35.375 0 .125-.325.65-.326.5-.8 1.15-.45.65-.85 1.175t-.9 1.025q-.501.5-1.275.5-.726 0-1-.325a1.16 1.16 0 0 1-.25-.725q0-.35.075-.725.075-.4.15-.675l-.1-.075a8 8 0 0 1-.675.7q-.45.425-1 .875a8.3 8.3 0 0 1-1.1.725q-.525.3-.95.3m9.766.35a.6.6 0 0 1-.275-.075q-.15-.075-.15-.2 0-.075.025-.175.024-.1.05-.175.274-.625.775-1.6l1.05-2.05a29 29 0 0 0 .925-2.025q.375-.925.375-1.425 0-.15-.075-.275-.05-.15-.25-.15-.325 0-.775.325-.425.3-.875.825-.25.3-.65.875-.4.55-.825 1.2l-.8 1.175a7 7 0 0 1-.55.775q-.176.25-.425.25-.175 0-.25-.125-.1-.124 0-.3.2-.375.6-1 .425-.625.875-1.3t.825-1.2.475-.675a6.7 6.7 0 0 1 1.025-1.05q.624-.5 1.425-.5 1.15 0 1.15 1.025 0 .475-.225 1.1t-.65 1.45q.8-.825 1.375-1.35.6-.525 1.175-.975.575-.475 1.2-.475.524 0 .875.375.375.375.375 1.1 0 .225-.05.525a5.5 5.5 0 0 1-.15.775 12.6 12.6 0 0 1 1.8-1.625q.975-.7 1.8-1.15.85-.476 1.325-.6.25-.075.475-.125.249-.05.45-.05.25 0 .4.125t.15.475-.45 1q-.45.625-1.1 1.45a37 37 0 0 0-1.2 1.625q-.576.825-.825 1.55-.125.425-.125.625 0 .675.625.675.425 0 1.025-.4.65-.425 1.4-1.375t1.4-1.95a.46.46 0 0 1 .4-.225q.174 0 .225.1.05.075.05.15 0 .124-.25.6-.225.476-.775 1.2-.7.925-1.275 1.475-.55.525-.9.725a2.7 2.7 0 0 1-.675.3q-.35.075-.7.075-.8 0-1.15-.325a1.13 1.13 0 0 1-.35-.825q0-.65.425-1.475.45-.825 1.05-1.65.6-.85 1.125-1.525.549-.675.75-.975a.4.4 0 0 0 .1-.25q0-.2-.275-.2-.15 0-.35.075A9.4 9.4 0 0 0 43.41 10.8q-1 .725-1.8 1.475-.776.75-1.2 1.25l-.275.95a3.9 3.9 0 0 1-.475 1.025q-.3.45-.775.45a.5.5 0 0 1-.375-.15.6.6 0 0 1-.125-.35q0-.65.4-1.2t.925-1.025q.125-.425.35-1.1.225-.7.225-1.175 0-.4-.2-.625a.55.55 0 0 0-.45-.225q-.326 0-.65.25a9.6 9.6 0 0 0-2.175 2.1q-.95 1.226-1.7 2.4-.4.574-.75 1.2-.326.626-.725 1.2a.6.6 0 0 1-.15.15.4.4 0 0 1-.225.05m24.032 2.125q-1.525 0-2.5-.625a3.7 3.7 0 0 1-1.4-1.7q-.45-1.05-.45-2.35 0-2 .975-4.15a11.2 11.2 0 0 1 1.925-2.85q1.2-1.325 2.7-2.1 1.5-.8 3.15-.8 1.275 0 2.025.35.75.325 1.075.875.35.525.35 1.125a2.7 2.7 0 0 1-.5 1.55q-.5.725-1.225.925-.025.025-.075.025-.025.025-.1.025a.2.2 0 0 0 .1-.025h.025l.05-.025q.35-.15.675-.775.35-.626.35-1.35 0-.8-.6-1.45t-2.225-.65q-1.45 0-2.825.775-1.35.775-2.45 2.05-1.075 1.275-1.75 2.75-.85 1.925-.85 3.75 0 1.65.85 2.8.849 1.15 2.7 1.15 1.074 0 2.225-.6 1.174-.6 2.35-1.55 1.2-.975 2.325-2.125a86 86 0 0 0 2.15-2.225q-.95.1-1.825.25a17 17 0 0 0-1.65.325h-.1q-.3 0-.3-.3a.4.4 0 0 1 .1-.25.38.38 0 0 1 .275-.175 57 57 0 0 1 3.975-.5q4.1-4.85 6.55-7.6t3.9-3.375q.575-.25 1.1-.25.55 0 .55.35t-.675 1.025q-.9.926-1.85 2.475a32 32 0 0 0-1.75 3.4 28 28 0 0 0-1.325 3.7h.575q.225-.025.4 0 .249.026.375.15.125.125.125.25 0 .3-.375.3h-.575a8 8 0 0 0-.65-.025q-.425 1.7-.425 3.125 0 .35.025.675.024.325.075.625.125.6.3 1.05.2.45.525.45.374 0 .95-.725.2-.225.375-.225.125 0 .2.1.075.125-.025.325-.55 1.125-1.475 1.125-.501 0-.975-.45-.45-.45-.65-1.45a7.8 7.8 0 0 1-.15-1.575q0-.7.075-1.45.1-.775.275-1.6-1.026 0-2.325.025-1.275 0-2.575.125a.5.5 0 0 1-.1.125 1 1 0 0 0-.125.1 67 67 0 0 1-2.225 2.375 33 33 0 0 1-2.55 2.3 13.4 13.4 0 0 1-2.625 1.725q-1.3.675-2.475.675M67.967 11.6q1.224-.075 2.4-.1 1.174-.05 2.1-.075.45-1.826 1.225-3.6A29.5 29.5 0 0 1 75.367 4.5q.9-1.525 1.7-2.5l.225-.3q.15-.225.275-.4.15-.2.1-.175-1.35.575-3.675 3.3a862 862 0 0 0-6.025 7.175m11.613 5.75q-.474 0-.825-.3-.325-.3-.325-.95 0-.525.2-1.075a6.4 6.4 0 0 1 .375-.9q.4-.8.85-1.475.45-.7.675-1-.45.025-1.075-.225-.6-.25-.85-.65-.325.475-.925 1.55-.6 1.05-1.525 2.225-.15.2-.35.2-.375 0-.375-.325 0-.125.075-.225.376-.525.925-1.35.576-.85 1.1-1.75.55-.925.875-1.65.1-.225.2-.475.126-.25.225-.475a.94.94 0 0 1 .275-.3q.175-.15.4-.15.2 0 .4.175.2.15.2.35 0 .225-.175.575-.15.325-.325.675-.174.35-.175.625 0 .225.375.3.376.075.55.075.675 0 1.125-.275t.625-.275q.15 0 .25.125.126.1.125.225 0 .175-.15.3a8.4 8.4 0 0 0-.825.925q-.474.6-.95 1.3-.474.7-.825 1.375-.099.25-.3.7-.174.45-.175.85 0 .675.475.675.201 0 .45-.15.276-.15.525-.35.6-.55 1.05-1.075.476-.55.95-1.225.476-.675 1.1-1.625.1-.175.35-.175.325 0 .325.3 0 .15-.15.425-.35.625-.925 1.375-.55.75-1.15 1.425-.575.65-1.025 1-.8.675-1.65.675m5.057.55q-.501 0-.75-.325a1.5 1.5 0 0 1-.326-.725 4.4 4.4 0 0 1-.075-.75q0-1.025.376-2.3.374-1.275.875-2.55a222 222 0 0 1 .95-2.275q-.45.025-1 .15-.525.125-.95.125-.35 0-.35-.3 0-.425.45-.5.55-.125 1.124-.2.576-.075 1.126-.125.15-.275.424-.85.275-.575.626-1.225.35-.675.674-1.2.35-.55.575-.775a.454.454 0 0 1 .626-.05q.124.1.124.25 0 .075-.075.2a8 8 0 0 0-1.05 1.7 92 92 0 0 1-.85 1.8q.45-.05.95-.1.501-.05.975-.05.15 0 .276.05.15.05.15.225 0 .15-.15.25a.85.85 0 0 1-.3.15q-.55.1-1.15.175-.576.05-1.15.125-.325.75-.75 1.675a42 42 0 0 0-.826 1.925q-.375.975-.625 1.9t-.25 1.675v.425q.026.275.125.5.1.225.3.225.426 0 .6-.225.175-.225.4-.225.175 0 .3.1.15.1.15.25 0 .225-.3.425-.275.2-.65.325t-.6.125M92.64 6.8a.9.9 0 0 1-.275-.05q-.15-.05-.15-.225 0-.125.075-.225.2-.375.725-.65.55-.275.825-.275.126 0 .275.075.15.05.15.225 0 .425-.525.775-.5.35-1.1.35m-2.8 10.225q-.849 0-1.35-.475-.5-.475-.5-1.325 0-.5.1-.975a6.5 6.5 0 0 1 .275-.975q.1-.274.4-.9.325-.65.75-1.35.426-.725.825-1.225.4-.5.7-.5.126 0 .325.1.225.075.225.25 0 .1-.1.25-.575.65-1.2 1.55-.624.9-1.05 1.875-.425.975-.425 1.875 0 1.15 1.1 1.15.75 0 1.5-.725.776-.724 1.7-1.95.276-.35.475-.675.226-.35.4-.6a.53.53 0 0 1 .375-.175q.3 0 .3.225 0 .275-.4.875-.399.6-.9 1.25-.975 1.2-1.7 1.825-.724.625-1.825.625m5.6 0q-.999 0-1.374-.6t-.375-1.45q0-.95.3-2.175.324-1.226.825-2.55.524-1.325 1.175-2.6.65-1.275 1.325-2.325T98.592 3.6q.525-.575 1-.875a5 5 0 0 1 1.05-.525q.375-.15.75-.15.5 0 .85.25.375.25.375.825 0 .55-.4 1.275-.4.7-1 1.425-.6.724-1.225 1.35-.626.625-1.1 1.025-1.05.874-2.025 1.65-.975.75-1.625 1.525-.35.975-.55 1.9-.2.9-.2 1.625 0 .65.225 1.075.25.425.85.425t1.15-.375q.549-.4 1.05-1 .525-.6.95-1.2.3-.424.575-.825l.475-.675q.1-.175.325-.175.275 0 .275.275 0 .175-.15.45-.3.5-.775 1.2t-1.075 1.375-1.35 1.125a2.93 2.93 0 0 1-1.575.45M99.192 4.1q-.55.6-1.174 1.55-.6.95-1.2 2.1t-1.1 2.325a24 24 0 0 1 1.45-1.225q.65-.525 1.3-1.1.574-.5 1.225-1.225.675-.75 1.25-1.55t.8-1.475q.075-.275.075-.425 0-.4-.4-.4-.1 0-.225.025a2 2 0 0 0-.225.075q-.45.175-.9.5a6.5 6.5 0 0 0-.875.825m1.939 12.925q-1 0-1.375-.6t-.375-1.45q0-.95.3-2.175.325-1.226.825-2.55.525-1.325 1.175-2.6t1.325-2.325T104.28 3.6q.525-.575 1-.875a5 5 0 0 1 1.05-.525q.375-.15.75-.15.5 0 .85.25.375.25.375.825 0 .55-.4 1.275-.4.7-1 1.425-.6.724-1.225 1.35-.625.625-1.1 1.025-1.05.874-2.025 1.65-.975.75-1.625 1.525-.35.975-.55 1.9-.2.9-.2 1.625 0 .65.225 1.075.25.425.85.425t1.15-.375q.55-.4 1.05-1 .525-.6.95-1.2.3-.424.575-.825l.475-.675q.1-.175.325-.175.275 0 .275.275 0 .175-.15.45-.3.5-.775 1.2t-1.075 1.375-1.35 1.125a2.93 2.93 0 0 1-1.575.45M104.88 4.1q-.55.6-1.175 1.55-.6.95-1.2 2.1a33 33 0 0 0-1.1 2.325 24 24 0 0 1 1.45-1.225q.65-.525 1.3-1.1.575-.5 1.225-1.225a18 18 0 0 0 1.25-1.55q.575-.8.8-1.475.075-.275.075-.425 0-.4-.4-.4-.1 0-.225.025a2 2 0 0 0-.225.075q-.45.175-.9.5a6.6 6.6 0 0 0-.875.825m2.239 13.15q-1.3 0-1.825-.75-.5-.776-.5-1.8 0-.75.375-1.775.374-1.05 1.025-2.05a6.7 6.7 0 0 1 1.55-1.65q.874-.675 1.875-.675.65 0 1.025.375.4.35.4.9 0 1.05-.675 2.025a5.4 5.4 0 0 1-1.75 1.6 4.5 4.5 0 0 1-2.3.625h-.3l-.3-.05q-.075.325-.075.75 0 .65.3 1.2.325.55 1.225.55.75 0 1.45-.375t1.175-.85.8-.85q.324-.375.675-.85.35-.5.875-1.3a.32.32 0 0 1 .3-.175q.35 0 .35.375 0 .125-.05.2-.675 1.15-1.5 2.2a8.1 8.1 0 0 1-1.85 1.7q-1 .65-2.275.65m-.625-3.95q.95 0 1.775-.5a4.5 4.5 0 0 0 1.375-1.3q.55-.776.55-1.575a.67.67 0 0 0-.15-.45q-.126-.175-.45-.175-.65 0-1.25.45-.6.425-1.1 1.1a11 11 0 0 0-.825 1.3q-.325.65-.475 1.1l.25.05zm8.413 4.05q-.474 0-.825-.3-.325-.3-.325-.95 0-.525.2-1.075.201-.574.375-.9.4-.8.85-1.475.45-.7.675-1-.45.025-1.075-.225-.6-.25-.85-.65-.325.475-.925 1.55-.6 1.05-1.525 2.225-.15.2-.35.2-.375 0-.375-.325 0-.125.075-.225.376-.525.925-1.35.576-.85 1.1-1.75.55-.925.875-1.65.1-.225.2-.475.126-.25.225-.475a.95.95 0 0 1 .275-.3q.175-.15.4-.15.2 0 .4.175.2.15.2.35 0 .225-.175.575-.15.325-.325.675t-.175.625q0 .225.375.3t.55.075q.675 0 1.125-.275t.625-.275q.15 0 .25.125.125.1.125.225 0 .175-.15.3a8.5 8.5 0 0 0-.825.925q-.474.6-.95 1.3-.474.7-.825 1.375-.099.25-.3.7-.174.45-.175.85 0 .675.475.675.201 0 .45-.15.276-.15.525-.35.6-.55 1.05-1.075.476-.55.95-1.225.476-.675 1.1-1.625.1-.175.35-.175.325 0 .325.3 0 .15-.15.425-.35.625-.925 1.375-.55.75-1.15 1.425-.575.65-1.025 1-.8.675-1.65.675m.482 8.975q-.85 0-1.325-.45-.5-.425-.5-1.1 0-.6.525-1.325t1.65-1.6q.375-.3 1.225-.85.825-.525 1.875-1.175l2.1-1.275q1.05-.625 1.875-1.125.825-.475 1.175-.7.225-.6.325-1.3.1-.725.1-1.3.024-.6 0-.875-.3.7-.925 1.525-.6.8-1.4 1.6a4 4 0 0 1-1.1.775q-.6.325-1.175.325-.55 0-.925-.375-.35-.375-.35-1.25 0-.575.125-1.15t.275-1q.174-.424.25-.525a.63.63 0 0 1-.125-.375q0-.45.325-1.05.35-.6.725-1.1.4-.5.5-.675.05-.1.2-.15a.8.8 0 0 1 .3-.075q.174 0 .3.1.15.1.15.25a.4.4 0 0 1-.05.15 33 33 0 0 0-1.525 2.925q-.65 1.45-.65 2.675 0 .475.175.675a.58.58 0 0 0 .425.175q.424 0 .925-.325a4 4 0 0 0 .775-.625 12.3 12.3 0 0 0 1.625-2.075q.7-1.125 1-1.75.075-.2.25-.45.2-.275.5-.275.274 0 .325.25.075.225.075.725 0 1.15-.1 2.1-.075.95-.2 1.6a8.8 8.8 0 0 0 1.55-1.675 51 51 0 0 0 1.3-1.875q.124-.2.325-.2.124 0 .225.1.125.1.125.25a.6.6 0 0 1-.075.25 31 31 0 0 1-1.65 2.4q-.876 1.125-2.2 2a15.9 15.9 0 0 1-1.925 3.775q-1.2 1.8-2.675 3.1-1.45 1.325-2.925 1.925-.951.4-1.8.4m0-.625q.3 0 .65-.1t.725-.25a10.2 10.2 0 0 0 2.7-1.75 14.6 14.6 0 0 0 2.175-2.375q.624-.875 1.1-1.825.474-.925.75-1.425-.45.25-1.225.7-.75.45-1.675 1l-1.8 1.1q-.876.55-1.575 1a16 16 0 0 0-1.075.75q-.8.625-1.25 1.175-.475.575-.475 1.075 0 .4.25.65.25.275.725.275" />
              </svg>
            </p>
          </div>
        </div>
      </section>
    </>
  );
}

const SectionHeader = ({
  children,
  ...props
}: HTMLProps<HTMLHeadingElement>) => (
  <h2
    className="mb-4 text-2xl font-medium text-slate-8 *:block *:text-contrast lg:mb-8"
    {...props}
  >
    {children}
  </h2>
);

const FrameHeader = ({ children }: { children: ReactNode }) => (
  <header className="flex items-center justify-between border-b border-inherit px-2 py-1 text-slate-8 md:px-3 md:py-1.5">
    <span className="relative size-2 rounded-full bg-[#ED6A5E] opacity-50 before:absolute before:inset-0 before:translate-x-3 before:rounded-full before:bg-[#F4BF4E] after:absolute after:inset-0 after:translate-x-6 after:rounded-full after:bg-[#61C653] md:size-2.5 md:before:translate-x-4 md:after:translate-x-8" />
    <small>{children}</small>
    <span className="size-2.5" />
  </header>
);
